import React, { useEffect, useMemo, useState } from 'react';
import { Search, ChevronRight, Check, Layers, X } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { useClientStore } from '@/client/stores/client';
import type { Category } from '@/client/types/entity-full';

// The public hierarchy only returns active categories; guard defensively in
// case an `active` flag is present.
const isActive = (t: Category) => {
  const a = (t as { active?: number | boolean | null }).active;
  return a == null || a === 1 || a === true;
};

const labelOf = (t: Category) => t.name_friendly || t.name_menu || t.name_system || `Category #${t.etid}`;

interface FlatNode {
  etid: number;
  label: string;
  depth: number;
  path: string;
  hasChildren: boolean;
}

/** Depth-first flatten of the category tree (active nodes only). */
const flatten = (nodes: Category[], depth = 0, parents: string[] = []): FlatNode[] => {
  const out: FlatNode[] = [];
  for (const n of nodes ?? []) {
    if (n.etid == null || !isActive(n)) continue;
    const label = labelOf(n);
    const children = (n.children ?? []).filter((c) => c.etid != null && isActive(c));
    out.push({ etid: n.etid, label, depth, path: [...parents, label].join(' › '), hasChildren: children.length > 0 });
    if (children.length) out.push(...flatten(children, depth + 1, [...parents, label]));
  }
  return out;
};

interface Props {
  open: boolean;
  onClose: () => void;
  /** Selected etid as a string, '' = all types. */
  value: string;
  onChange: (etid: string) => void;
}

/**
 * Side drawer for choosing a listing category from the full entity-type tree.
 * Collapsed tree by default (compact for a large hierarchy); typing switches to
 * a flat, path-annotated search. Single-select — picking applies and closes.
 *
 * Sourced from the shared category tree in the client store
 * (`/public/entity/type/hierarchy`), the same one HighlightEtidPicker uses.
 */
const CategoryFilterDrawer: React.FC<Props> = ({ open, onClose, value, onChange }) => {
  const categories = useClientStore((s) => s.categories);
  const fetchCategories = useClientStore((s) => s.fetchCategories);

  useEffect(() => {
    if (open && !categories) fetchCategories();
  }, [open, categories, fetchCategories]);

  const roots = useMemo(
    () => (categories ?? []).filter((t) => t.etid != null && isActive(t)),
    [categories],
  );
  const flat = useMemo(() => flatten(roots), [roots]);

  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const q = query.trim().toLowerCase();
  const results = useMemo(
    () => (q ? flat.filter((n) => n.label.toLowerCase().includes(q) || n.path.toLowerCase().includes(q)) : []),
    [flat, q],
  );

  const toggle = (etid: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(etid) ? next.delete(etid) : next.add(etid);
      return next;
    });

  const pick = (etid: string) => {
    onChange(etid);
    onClose();
  };

  const selected = value ? flat.find((n) => String(n.etid) === value) : null;

  // Collapsed tree: a node shows only when all its ancestors are expanded.
  const visibleTreeNodes = useMemo(() => {
    if (q) return [];
    const out: FlatNode[] = [];
    const openPath: number[] = [];
    for (const n of flat) {
      openPath.length = n.depth;
      const parentVisible = n.depth === 0 || openPath.slice(0, n.depth).every((e) => expanded.has(e));
      if (parentVisible) out.push(n);
      openPath[n.depth] = n.etid;
    }
    return out;
  }, [flat, expanded, q]);

  const Row: React.FC<{ n: FlatNode; showPath?: boolean }> = ({ n, showPath }) => {
    const isSel = String(n.etid) === value;
    return (
      <div
        className={`flex items-center gap-1 rounded-lg pr-2 ${isSel ? 'bg-indigo-50' : 'hover:bg-gray-50'}`}
        style={{ paddingLeft: showPath ? 8 : 8 + n.depth * 16 }}
      >
        {!showPath && n.hasChildren ? (
          <button
            type="button"
            onClick={() => toggle(n.etid)}
            className="rounded p-1 text-gray-400 hover:text-gray-700"
            aria-label={expanded.has(n.etid) ? 'Collapse' : 'Expand'}
          >
            <ChevronRight className={`h-3.5 w-3.5 transition-transform ${expanded.has(n.etid) ? 'rotate-90' : ''}`} />
          </button>
        ) : (
          <span className="w-6" />
        )}
        <button
          type="button"
          onClick={() => pick(String(n.etid))}
          className="flex min-w-0 flex-1 items-center justify-between py-2 text-left"
        >
          <span className="min-w-0">
            <span className={`block truncate text-sm ${isSel ? 'font-medium text-indigo-700' : 'text-gray-800'}`}>
              {n.label}
            </span>
            {showPath && n.path.includes('›') && (
              <span className="block truncate text-[11px] text-gray-400">{n.path}</span>
            )}
          </span>
          {isSel && <Check className="ml-2 h-4 w-4 shrink-0 text-indigo-600" />}
        </button>
      </div>
    );
  };

  const loading = !categories;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full max-w-md p-0 sm:max-w-md">
        <SheetHeader className="border-b border-gray-100 px-5 py-4 text-left">
          <SheetTitle className="flex items-center gap-2 text-base">
            <Layers className="h-4 w-4 text-indigo-500" />
            Filter by category
          </SheetTitle>
          <SheetDescription className="text-xs">
            {selected ? <>Selected: <span className="font-medium text-gray-700">{selected.label}</span></> : 'Showing all types'}
          </SheetDescription>
        </SheetHeader>

        <div className="flex h-[calc(100%-5rem)] flex-col">
          {/* Search */}
          <div className="border-b border-gray-100 p-4">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search categories…"
                className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-8 text-sm focus:border-transparent focus:ring-2 focus:ring-indigo-500"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="absolute right-2 top-2.5 rounded p-0.5 text-gray-400 hover:text-gray-700"
                  aria-label="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto px-3 py-2">
            {/* All types */}
            <button
              type="button"
              onClick={() => pick('')}
              className={`mb-1 flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm ${
                value === '' ? 'bg-indigo-50 font-medium text-indigo-700' : 'text-gray-800 hover:bg-gray-50'
              }`}
            >
              All types
              {value === '' && <Check className="h-4 w-4 text-indigo-600" />}
            </button>

            {loading ? (
              <div className="space-y-1.5 px-1 py-2">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            ) : q ? (
              results.length > 0 ? (
                results.map((n) => <Row key={n.etid} n={n} showPath />)
              ) : (
                <p className="px-3 py-8 text-center text-sm text-gray-400">No categories match “{query}”.</p>
              )
            ) : flat.length > 0 ? (
              visibleTreeNodes.map((n) => <Row key={n.etid} n={n} />)
            ) : (
              <p className="px-3 py-8 text-center text-sm text-gray-400">No categories available.</p>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CategoryFilterDrawer;
